import type { DocumentPickerAsset } from "expo-document-picker";
import type { ImagePickerAsset } from "expo-image-picker";
import { useCallback, useState } from "react";
import { FileUp as FileUpIcon, Image as ImageIcon } from "@tamagui/lucide-icons";
import { isWeb, Modal, ResourceCard, type UseFilePickerOptions, useFilePicker, useImagePicker, YStack } from "@medbillai/ui";
import { generateFileName, isSupportedImageType } from "@medbillai/utils/images";
import { analyticsService } from "../../lib/analytics";
import type { DocumentScanEntryPoint } from "../../lib/analytics/types";
import type { UploadError, UploadFile } from "../../provider/documents";
import { CameraPickerButton } from "./components/CameraPickerButton";
import { assetToBlob } from "./utils/assetToBlob";
import { openScanbot } from "./utils/open-scanbot/openScanbot";
export type UploadDocumentSheetProps = {
  open: boolean;
  onUploadError: (error: UploadError) => void;
  onOpenChange: (open: boolean) => void;
  onCameraPermissionCheckStart: () => void;
  onCameraPermissionCheckComplete: () => void;
  onCameraPermissionDenied?: () => void;
  onFilesLoaded: (files: UploadFile[]) => Promise<void>;
  cleanupBeforeUpload?: () => void;
  title?: string;
  subtitle?: string;
  /** Case id when scanning from a case-scoped surface. */
  issueId?: string;
  /** Product surface that opened the upload sheet; defaults to OTHER. */
  entryPoint?: DocumentScanEntryPoint;
  /**
   * File-picker constraints for "Upload a file". Defaults preserve chat's
   * behavior: single PDF on web, the picker's own defaults on native.
   */
  filePickerOptions?: Pick<UseFilePickerOptions, "accept" | "multiple">;
};
export const UploadDocumentSheet = ({
  open,
  onUploadError,
  onOpenChange,
  onCameraPermissionCheckStart,
  onCameraPermissionCheckComplete,
  onCameraPermissionDenied,
  onFilesLoaded,
  cleanupBeforeUpload,
  title = "Upload",
  subtitle,
  issueId,
  entryPoint = "OTHER",
  filePickerOptions
}: UploadDocumentSheetProps) => {
  const [useCamera, setUseCamera] = useState<boolean>(false);
  const openCamera = async () => {
    try {
      setUseCamera(true);
      const scannedImages = await openScanbot({
        entryPoint,
        issueId
      });
      if (scannedImages.status === "CANCELED") {
        return;
      }
      if (scannedImages.images.length > 0) {
        await multiPhotoUpload(scannedImages.images);
      } else {
        throw new Error("No valid images found");
      }
    } catch (error) {
      console.error("Error during document scanning:", error);
      onUploadError({
        error: error instanceof Error ? error : new Error(String(error))
      });
    } finally {
      setUseCamera(false);
    }
  };
  const onError = useCallback((error: unknown) => {
    if (error instanceof Error) {
      onUploadError({
        error
      });
      return;
    }
    onUploadError({
      error: new Error("Unknown error")
    });
  }, [onUploadError]);
  const multiPhotoUpload = useCallback(async (images: {
    id: string;
    path: string;
  }[]) => {
    if (images.length === 0) {
      return;
    }
    cleanupBeforeUpload?.();
    analyticsService.trackEvent("ClickedUpload", {
      type: "CAMERA"
    });
    try {
      const uploadFiles = await Promise.all(images.reverse().map(async (image, idx) => {
        const response = await fetch(image.path);
        const blob = await response.blob();
        if (!isSupportedImageType(blob.type)) {
          onUploadError({
            message: "This file type is not currently supported."
          });
          throw new Error("Unsupported file type");
        }
        const filename = `${idx}-${generateFileName(blob.type)}`;
        return {
          name: filename,
          sizeBytes: blob.size,
          contentType: blob.type,
          blob
        } as UploadFile;
      }));

      // The consumer drives the close via its own `open` state. We
      // deliberately do NOT call `onOpenChange(false)` here — doing so
      // synchronously, mid-upload, races the dismiss animation and tears
      // the modal out (ENG-6644).
      await onFilesLoaded(uploadFiles);
    } catch (error) {
      onError(error);
    }
  }, [onError, onFilesLoaded, onUploadError, cleanupBeforeUpload]);
  const onImageSelected = async (images: ImagePickerAsset[]): Promise<void> => {
    analyticsService.trackEvent("ClickedUpload", {
      type: "GALLERY"
    });
    if (images.length === 0) {
      onOpenChange(false);
      return;
    }
    cleanupBeforeUpload?.();
    try {
      const uploadFiles = await Promise.all(images.map(async (image, idx) => {
        const blob = await assetToBlob(image);
        if (!isSupportedImageType(blob.type)) {
          onUploadError({
            message: "This file type is not currently supported."
          });
          throw new Error("Unsupported file type");
        }
        const filename = `${idx}-${generateFileName(blob.type)}`;
        return {
          name: filename,
          sizeBytes: blob.size,
          contentType: blob.type,
          blob
        } as UploadFile;
      }));
      // See multiPhotoUpload for why we don't close here (ENG-6644).
      await onFilesLoaded(uploadFiles);
    } catch (error) {
      onError(error);
    }
  };
  const onFilesSelected = async (files: DocumentPickerAsset[]): Promise<void> => {
    analyticsService.trackEvent("ClickedUpload", {
      type: "FILE"
    });
    if (files.length === 0) {
      onOpenChange(false);
      return;
    }
    cleanupBeforeUpload?.();
    try {
      const uploadFiles: UploadFile[] = [];
      for (const file of files) {
        const blob = await assetToBlob(file);
        uploadFiles.push({
          name: file.name,
          sizeBytes: file.size ?? blob.size,
          contentType: blob.type,
          blob
        });
      }

      // See multiPhotoUpload for why we don't close here (ENG-6644).
      await onFilesLoaded(uploadFiles);
    } catch (error) {
      onError(error);
    }
  };
  const {
    pickImage,
    isLoading: imageLoading
  } = useImagePicker({
    onImageSelected
  });
  const {
    pickFile,
    isLoading: fileLoading
  } = useFilePicker({
    onFilesSelected,
    ...(filePickerOptions ?? (isWeb ? {
      accept: ["application/pdf"],
      multiple: false
    } : {}))
  });
  const imageLabel = isWeb ? "Upload photos" : "Choose from library";
  return <Modal open={open} onOpenChange={onOpenChange} size="small" headerProps={subtitle ? {
    title,
    variant: "largeTitle",
    subtitle
  } : {
    title
  }} data-sentry-element="Modal" data-sentry-component="UploadDocumentSheet" data-sentry-source-file="UploadDocumentSheet.tsx">
      <YStack gap="$sp1" paddingVertical={isWeb ? "$sp2" : 0} data-sentry-element="YStack" data-sentry-source-file="UploadDocumentSheet.tsx">
        {!isWeb ? <CameraPickerButton isLoading={useCamera} onPermissionCheckStart={onCameraPermissionCheckStart} onPermissionCheckComplete={onCameraPermissionCheckComplete} onPermissionDenied={onCameraPermissionDenied} setIsLoading={setUseCamera} openCamera={openCamera} /> : null}
        <ResourceCard kind="button" icon={ImageIcon} label={imageLabel} loading={imageLoading} disabled={imageLoading} size="large" onPress={pickImage} aria-label={imageLabel} data-sentry-element="ResourceCard" data-sentry-source-file="UploadDocumentSheet.tsx" />
        <ResourceCard kind="button" icon={FileUpIcon} label="Upload a file" loading={fileLoading} disabled={fileLoading} size="large" onPress={pickFile} aria-label="Upload a file" testID="case-preview.choose-file" data-sentry-element="ResourceCard" data-sentry-source-file="UploadDocumentSheet.tsx" />
      </YStack>
    </Modal>;
};